import React, { useEffect, useState } from "react";
import api from "../axios";

const emptyAdAccount = {
  ad_account_name: "",
  ad_account_id: "",
  facebook_page_id: "",
  phone_number_id: "",
  instagram_account_id: "",
  website_url: "",
  access_token: "",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const emptyUser = {
    email: "",
    password: "",
    wallet: 0,
    business_name: "",
    app_id: "",
    phone_number_id: "",
    waba_id: "",
    whatsapp_token: ""
  };

  const [newUser, setNewUser] = useState(emptyUser);

  // ---- AI Ads management (per user, expandable panel) ----
  const [expandedId, setExpandedId] = useState(null);
  const [metaDraft, setMetaDraft] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [newAcc, setNewAcc] = useState(emptyAdAccount);
  const [panelLoading, setPanelLoading] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [newNumber, setNewNumber] = useState({ meta_ad_account_id: "", phone_number_id: "" });
  const [numberSaving, setNumberSaving] = useState(false);
  const [socialPages, setSocialPages] = useState([]);
  const [newPage, setNewPage] = useState({
    page_name: "",
    facebook_page_id: "",
    page_access_token: "",
    instagram_account_id: "",
  });
  const [pageSaving, setPageSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to load users");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();

    try {
      await api.post("/admin/users", newUser);
      alert("Client created ✅");
      setNewUser(emptyUser);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to create client");
    }
  };

  const updateWallet = async (id, wallet) => {
    try {
      await api.put(`/admin/users/${id}/wallet`, { wallet });
      alert("Wallet updated ✅");
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Wallet update failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/status`, { status });
      alert(`User ${status} ✅`);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Status update failed");
    }
  };

  const [subscriptionForm, setSubscriptionForm] = useState({});

  const updateSubscription = async (userId, packageType, customExpiry) => {
    try {
      await api.put(`/admin/users/${userId}/subscription`, {
        package_type: packageType,
        custom_expiry: customExpiry || null,
      });
      alert("Subscription updated ✅");
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Update failed");
    }
  };

  const toggleLock = async (userId, currentlyLocked) => {
    try {
      await api.put(`/admin/users/${userId}/subscription-lock`, {
        locked: !currentlyLocked,
      });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Update failed");
    }
  };

  const updatePermissions = async (user, field) => {
    try {
      const payload = {
        can_use_inbox: Number(user.can_use_inbox || 0),
        can_use_ai_chatbot: Number(user.can_use_ai_chatbot || 0),
        can_use_ai_ads: Number(user.can_use_ai_ads || 0),
        can_use_products: Number(user.can_use_products || 0),
        can_use_orders: Number(user.can_use_orders || 0),
        can_use_poster: Number(user.can_use_poster || 0),
        [field]: Number(user[field] || 0) === 1 ? 0 : 1
      };

      await api.put(`/admin/users/${user.id}/permissions`, payload);

      alert("Permissions updated ✅");
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Permission update failed");
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.delete(`/admin/users/${id}`);
      alert("User deleted ✅");
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.msg || "Delete failed");
    }
  };

  useEffect(() => {
    setLoading(true);
    loadUsers().finally(() => setLoading(false));
  }, []);

  // ---------- AI Ads panel helpers ----------
  const openAiAdsPanel = async (userId) => {
    if (expandedId === userId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(userId);
    setPanelLoading(true);
    setNewAcc(emptyAdAccount);

    try {
      const [metaRes, accRes, numRes, pagesRes] = await Promise.all([
        api.get(`/admin/users/${userId}/meta`),
        api.get(`/admin/users/${userId}/ad-accounts`),
        api.get(`/admin/users/${userId}/whatsapp-numbers`),
        api.get(`/admin/users/${userId}/social-pages`),
      ]);

      setMetaDraft(
        metaRes.data || {
          business_name: "",
          app_id: "",
          phone_number_id: "",
          waba_id: "",
          gemini_api_key: "",
          status: "active",
        }
      );
      setAdAccounts(accRes.data || []);
      setWhatsappNumbers(numRes.data || []);
      setSocialPages(pagesRes.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to load AI Ads settings");
    } finally {
      setPanelLoading(false);
    }
  };

const saveGeminiKey = async (userId) => {
    if (!metaDraft?.phone_number_id || !metaDraft?.waba_id) {
      alert(
        "Is user ke liye pehle WhatsApp Phone Number ID aur WABA ID set hona chahiye (upar wale form/row se), tabhi keys save hongi."
      );
      return;
    }

    try {
      await api.put(`/admin/users/${userId}/meta`, {
        business_name: metaDraft.business_name,
        app_id: metaDraft.app_id,
        phone_number_id: metaDraft.phone_number_id,
        waba_id: metaDraft.waba_id,
        gemini_api_key: metaDraft.gemini_api_key,
        openai_api_key: metaDraft.openai_api_key,
        status: metaDraft.status || "active",
      });
      alert("API keys saved ✅");
    } catch (err) {
      alert(err.response?.data?.msg || "Key save failed");
    }
  };

  const addAdAccount = async (userId) => {
    if (!newAcc.ad_account_id || !newAcc.access_token) {
      alert("Ad Account ID aur Access Token dono zaroori hain.");
      return;
    }

    try {
      await api.post(`/admin/users/${userId}/ad-accounts`, newAcc);
      alert("Ad Account saved ✅");
      setNewAcc(emptyAdAccount);
      const res = await api.get(`/admin/users/${userId}/ad-accounts`);
      setAdAccounts(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Ad account save failed");
    }
  };

  const toggleAdAccountStatus = async (userId, acc) => {
    try {
      await api.put(`/admin/users/${userId}/ad-accounts/${acc.id}`, {
        status: acc.status === "active" ? "inactive" : "active",
      });
      const res = await api.get(`/admin/users/${userId}/ad-accounts`);
      setAdAccounts(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Update failed");
    }
  };

  const deleteAdAccount = async (userId, accId) => {
    if (!window.confirm("Is Ad Account ko delete karna hai?")) return;
    try {
      await api.delete(`/admin/users/${userId}/ad-accounts/${accId}`);
      const res = await api.get(`/admin/users/${userId}/ad-accounts`);
      setAdAccounts(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Delete failed");
    }
  };

  const addWhatsappNumber = async (userId) => {
    if (!newNumber.meta_ad_account_id || !newNumber.phone_number_id) {
      alert("Ad Account select karein aur Phone Number ID daalein.");
      return;
    }
    setNumberSaving(true);
    try {
      await api.post(`/admin/users/${userId}/whatsapp-numbers`, newNumber);
      alert("Number verify ho gaya aur save ho gaya ✅");
      setNewNumber({ meta_ad_account_id: "", phone_number_id: "" });
      const res = await api.get(`/admin/users/${userId}/whatsapp-numbers`);
      setWhatsappNumbers(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Number verify/save nahi ho paya");
    } finally {
      setNumberSaving(false);
    }
  };

  const toggleNumberStatus = async (userId, num) => {
    try {
      await api.put(`/admin/users/${userId}/whatsapp-numbers/${num.id}`, {
        status: num.status === "active" ? "inactive" : "active",
      });
      const res = await api.get(`/admin/users/${userId}/whatsapp-numbers`);
      setWhatsappNumbers(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Update failed");
    }
  };

  const deleteWhatsappNumber = async (userId, numId) => {
    if (!window.confirm("Ye number delete karna hai?")) return;
    try {
      await api.delete(`/admin/users/${userId}/whatsapp-numbers/${numId}`);
      const res = await api.get(`/admin/users/${userId}/whatsapp-numbers`);
      setWhatsappNumbers(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Delete failed");
    }
  };

  const addSocialPage = async (userId) => {
    if (!newPage.facebook_page_id || !newPage.page_access_token) {
      alert("Facebook Page ID aur Page Access Token dono zaroori hain.");
      return;
    }
    setPageSaving(true);
    try {
      await api.post(`/admin/users/${userId}/social-pages`, newPage);
      alert("Page verify ho gaya aur save ho gaya ✅");
      setNewPage({ page_name: "", facebook_page_id: "", page_access_token: "", instagram_account_id: "" });
      const res = await api.get(`/admin/users/${userId}/social-pages`);
      setSocialPages(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Page verify/save nahi ho paya");
    } finally {
      setPageSaving(false);
    }
  };

  const togglePageFeature = async (userId, page, field) => {
    try {
      await api.put(`/admin/users/${userId}/social-pages/${page.id}`, {
        [field]: Number(page[field] || 0) === 1 ? 0 : 1,
      });
      const res = await api.get(`/admin/users/${userId}/social-pages`);
      setSocialPages(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Update failed");
    }
  };

  const deleteSocialPage = async (userId, pageId) => {
    if (!window.confirm("Ye page delete karna hai?")) return;
    try {
      await api.delete(`/admin/users/${userId}/social-pages/${pageId}`);
      const res = await api.get(`/admin/users/${userId}/social-pages`);
      setSocialPages(res.data || []);
    } catch (err) {
      alert(err.response?.data?.msg || "Delete failed");
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold">Admin Users</h1>

      <form
        onSubmit={createUser}
        className="mt-6 bg-white p-4 rounded shadow grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <h2 className="text-xl font-bold md:col-span-2">Add New Client</h2>

        <input
          className="border p-2"
          placeholder="Client Email"
          value={newUser.email}
          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
        />

        <input
          className="border p-2"
          placeholder="Password"
          type="password"
          value={newUser.password}
          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
        />

        <input
          className="border p-2"
          placeholder="Wallet"
          type="number"
          value={newUser.wallet}
          onChange={(e) => setNewUser({ ...newUser, wallet: e.target.value })}
        />

        <input
          className="border p-2"
          placeholder="Business Name"
          value={newUser.business_name}
          onChange={(e) =>
            setNewUser({ ...newUser, business_name: e.target.value })
          }
        />

        <input
          className="border p-2"
          placeholder="Meta App ID"
          value={newUser.app_id}
          onChange={(e) => setNewUser({ ...newUser, app_id: e.target.value })}
        />

        <input
          className="border p-2"
          placeholder="Phone Number ID"
          value={newUser.phone_number_id}
          onChange={(e) =>
            setNewUser({ ...newUser, phone_number_id: e.target.value })
          }
        />

        <input
          className="border p-2"
          placeholder="WABA ID"
          value={newUser.waba_id}
          onChange={(e) => setNewUser({ ...newUser, waba_id: e.target.value })}
        />

        <input
          className="border p-2 md:col-span-2"
          placeholder="WhatsApp Token"
          value={newUser.whatsapp_token}
          onChange={(e) =>
            setNewUser({ ...newUser, whatsapp_token: e.target.value })
          }
        />

        <button className="bg-blue-600 text-white px-4 py-2 rounded md:col-span-2">
          Add Client
        </button>
      </form>

      {loading ? (
        <p className="mt-4">Loading...</p>
      ) : (
        <div className="mt-6 overflow-x-auto bg-white shadow rounded">
          <table className="w-full border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 border">ID</th>
                <th className="p-3 border">Email</th>
                <th className="p-3 border">Role</th>
                <th className="p-3 border">Status</th>
                <th className="p-3 border">Wallet</th>
                <th className="p-3 border">Business</th>
                <th className="p-3 border">App ID</th>
                <th className="p-3 border">Phone ID</th>
                <th className="p-3 border">WABA ID</th>
                <th className="p-3 border">Token</th>
                <th className="p-3 border">Inbox Access</th>
                <th className="p-3 border">AI Chatbot</th>
                <th className="p-3 border">AI Ads</th>
                <th className="p-3 border">Products</th>
                <th className="p-3 border">Orders</th>
                <th className="p-3 border">Poster Planner</th>
                <th className="p-3 border">Update Wallet</th>
                <th className="p-3 border">Block</th>
                <th className="p-3 border">Subscription</th>
                <th className="p-3 border">AI Ads Setup</th>
                <th className="p-3 border">Delete</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => (
                <React.Fragment key={u.id}>
                  <tr className="text-center">
                    <td className="p-3 border">{u.id}</td>
                    <td className="p-3 border">{u.email}</td>
                    <td className="p-3 border">{u.role}</td>
                    <td className="p-3 border">{u.status}</td>
                    <td className="p-3 border">{u.wallet}</td>
                    <td className="p-3 border">{u.business_name || "-"}</td>
                    <td className="p-3 border">{u.app_id || "-"}</td>
                    <td className="p-3 border">{u.phone_number_id || "-"}</td>
                    <td className="p-3 border">{u.waba_id || "-"}</td>
                    <td className="p-3 border">{u.token_masked || "-"}</td>

                    <td className="p-3 border">
                      <button
                        onClick={() => updatePermissions(u, "can_use_inbox")}
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_inbox || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_inbox || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() =>
                          updatePermissions(u, "can_use_ai_chatbot")
                        }
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_ai_chatbot || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_ai_chatbot || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => updatePermissions(u, "can_use_ai_ads")}
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_ai_ads || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_ai_ads || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => updatePermissions(u, "can_use_products")}
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_products || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_products || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => updatePermissions(u, "can_use_orders")}
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_orders || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_orders || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => updatePermissions(u, "can_use_poster")}
                        className={`px-3 py-1 rounded text-white ${
                          Number(u.can_use_poster || 0) === 1
                            ? "bg-green-600"
                            : "bg-gray-500"
                        }`}
                      >
                        {Number(u.can_use_poster || 0) === 1 ? "ON" : "OFF"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <input
                        type="number"
                        defaultValue={u.wallet}
                        className="border p-2 w-24"
                        onBlur={(e) => updateWallet(u.id, e.target.value)}
                      />
                    </td>

                    <td className="p-3 border">
                      {u.status === "blocked" ? (
                        <button
                          onClick={() => updateStatus(u.id, "active")}
                          className="bg-green-600 text-white px-3 py-1 rounded"
                        >
                          Unblock
                        </button>
                      ) : (
                        <button
                          onClick={() => updateStatus(u.id, "blocked")}
                          className="bg-red-600 text-white px-3 py-1 rounded"
                        >
                          Block
                        </button>
                      )}
                    </td>

                    <td className="p-3 border text-xs">
                      <div className="mb-1">
                        {u.subscription_locked ? (
                          <span className="text-red-600 font-semibold">🔒 Locked</span>
                        ) : u.subscription_expires_at && new Date(u.subscription_expires_at) < new Date() ? (
                          <span className="text-red-600 font-semibold">⏰ Expired</span>
                        ) : u.subscription_expires_at ? (
                          <span className="text-green-600 font-semibold">
                            ✅ {new Date(u.subscription_expires_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-gray-400">No limit</span>
                        )}
                      </div>

                      <select
                        className="border p-1 text-xs w-full mb-1"
                        defaultValue=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          if (e.target.value === "custom") return;
                          updateSubscription(u.id, e.target.value);
                          e.target.value = "";
                        }}
                      >
                        <option value="">Set Package...</option>
                        <option value="1_month">1 Month</option>
                        <option value="2_month">2 Months</option>
                        <option value="3_month">3 Months</option>
                        <option value="none">No Limit (Remove)</option>
                      </select>

                      <div className="flex gap-1">
                        <input
                          type="date"
                          className="border p-1 text-xs flex-1"
                          onChange={(e) =>
                            setSubscriptionForm({ ...subscriptionForm, [u.id]: e.target.value })
                          }
                        />
                        <button
                          onClick={() => updateSubscription(u.id, null, subscriptionForm[u.id])}
                          className="bg-gray-700 text-white px-2 rounded text-xs"
                        >
                          Set
                        </button>
                      </div>

                      <button
                        onClick={() => toggleLock(u.id, u.subscription_locked)}
                        className={`mt-1 w-full px-2 py-1 rounded text-xs text-white ${
                          u.subscription_locked ? "bg-green-600" : "bg-red-600"
                        }`}
                      >
                        {u.subscription_locked ? "Unlock" : "Lock Now"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => openAiAdsPanel(u.id)}
                        className="bg-purple-600 text-white px-3 py-1 rounded"
                      >
                        {expandedId === u.id ? "Close" : "Manage"}
                      </button>
                    </td>

                    <td className="p-3 border">
                      <button
                        onClick={() => deleteUser(u.id)}
                        className="bg-black text-white px-3 py-1 rounded"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>

                  {expandedId === u.id && (
                    <tr>
                      <td colSpan="16" className="p-4 border bg-purple-50 text-left">
                        {panelLoading ? (
                          <p>Loading AI Ads settings...</p>
                        ) : (
                          <div className="space-y-6">
                            <div>
                              <h3 className="font-bold text-purple-800 mb-2">
                                Gemini API Key ({u.email})
                              </h3>
                              <div className="flex flex-wrap gap-2 items-center">
                                <input
                                  className="border p-2 flex-1 min-w-[280px]"
                                  placeholder="Gemini API Key (AIzaSy...)"
                                  value={metaDraft?.gemini_api_key || ""}
                                  onChange={(e) =>
                                    setMetaDraft({
                                      ...metaDraft,
                                      gemini_api_key: e.target.value,
                                    })
                                  }
                                />
                                <button
                                  onClick={() => saveGeminiKey(u.id)}
                                  className="bg-purple-600 text-white px-4 py-2 rounded"
                                >
                                  Save Gemini Key
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-2 items-center mt-2">
                                <input
                                  className="border p-2 flex-1 min-w-[280px]"
                                  placeholder="OpenAI API Key (sk-...) — image generation ke liye"
                                  value={metaDraft?.openai_api_key || ""}
                                  onChange={(e) =>
                                    setMetaDraft({
                                      ...metaDraft,
                                      openai_api_key: e.target.value,
                                    })
                                  }
                                />
                                <button
                                  onClick={() => saveGeminiKey(u.id)}
                                  className="bg-black text-white px-4 py-2 rounded"
                                >
                                  Save OpenAI Key
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Ye key isi user ke WhatsApp settings (phone_number_id: {" "}
                                {metaDraft?.phone_number_id || "not set"}) ke saath
                                jud kar save hogi.
                              </p>
                            </div>

                            <div>
                              <h3 className="font-bold text-purple-800 mb-2">
                                Meta Ad Accounts (AI Ads Studio ke liye)
                              </h3>

                              <div className="overflow-x-auto">
                                <table className="w-full border text-xs mb-3 bg-white">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="p-2 border">Name</th>
                                      <th className="p-2 border">Ad Account ID</th>
                                      <th className="p-2 border">Page ID</th>
                                      <th className="p-2 border">Phone No ID</th>
                                      <th className="p-2 border">Instagram ID</th>
                                      <th className="p-2 border">Token</th>
                                      <th className="p-2 border">Status</th>
                                      <th className="p-2 border">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {adAccounts.map((acc) => (
                                      <tr key={acc.id} className="text-center">
                                        <td className="p-2 border">{acc.ad_account_name}</td>
                                        <td className="p-2 border">{acc.ad_account_id}</td>
                                        <td className="p-2 border">{acc.facebook_page_id || "-"}</td>
                                        <td className="p-2 border">{acc.phone_number_id || "-"}</td>
                                        <td className="p-2 border">{acc.instagram_account_id || "-"}</td>
                                        <td className="p-2 border">{acc.token_masked || "-"}</td>
                                        <td className="p-2 border">{acc.status}</td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => toggleAdAccountStatus(u.id, acc)}
                                            className={`px-2 py-1 rounded text-white mr-1 ${
                                              acc.status === "active" ? "bg-green-600" : "bg-gray-500"
                                            }`}
                                          >
                                            {acc.status === "active" ? "Active" : "Inactive"}
                                          </button>
                                          <button
                                            onClick={() => deleteAdAccount(u.id, acc.id)}
                                            className="px-2 py-1 rounded text-white bg-red-600"
                                          >
                                            Del
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                    {!adAccounts.length && (
                                      <tr>
                                        <td colSpan="8" className="p-2 text-center text-gray-500">
                                          Koi ad account connect nahi hai
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white p-3 rounded border grid md:grid-cols-3 gap-2">
                                <input
                                  className="border p-2"
                                  placeholder="Ad Account Name"
                                  value={newAcc.ad_account_name}
                                  onChange={(e) => setNewAcc({ ...newAcc, ad_account_name: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Ad Account ID (act_xxxx ya sirf number)"
                                  value={newAcc.ad_account_id}
                                  onChange={(e) => setNewAcc({ ...newAcc, ad_account_id: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Facebook Page ID"
                                  value={newAcc.facebook_page_id}
                                  onChange={(e) => setNewAcc({ ...newAcc, facebook_page_id: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="WhatsApp Phone Number ID"
                                  value={newAcc.phone_number_id}
                                  onChange={(e) => setNewAcc({ ...newAcc, phone_number_id: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Instagram Account ID (optional)"
                                  value={newAcc.instagram_account_id}
                                  onChange={(e) => setNewAcc({ ...newAcc, instagram_account_id: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Website URL (optional)"
                                  value={newAcc.website_url}
                                  onChange={(e) => setNewAcc({ ...newAcc, website_url: e.target.value })}
                                />
                                <input
                                  className="border p-2 md:col-span-2"
                                  placeholder="Meta Access Token (long-lived)"
                                  value={newAcc.access_token}
                                  onChange={(e) => setNewAcc({ ...newAcc, access_token: e.target.value })}
                                />
                                <button
                                  onClick={() => addAdAccount(u.id)}
                                  className="bg-purple-600 text-white px-4 py-2 rounded"
                                >
                                  + Add Ad Account
                                </button>
                              </div>
                            </div>

                            <div>
                              <h3 className="font-bold text-purple-800 mb-2">
                                Verified WhatsApp Numbers (AI Ads ke liye)
                              </h3>

                              <div className="overflow-x-auto">
                                <table className="w-full border text-xs mb-3 bg-white">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="p-2 border">Number</th>
                                      <th className="p-2 border">Verified Name</th>
                                      <th className="p-2 border">Ad Account</th>
                                      <th className="p-2 border">Status</th>
                                      <th className="p-2 border">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {whatsappNumbers.map((num) => {
                                      const acc = adAccounts.find(
                                        (a) => a.id === num.meta_ad_account_id
                                      );
                                      return (
                                        <tr key={num.id} className="text-center">
                                          <td className="p-2 border">
                                            {num.display_phone_number || num.phone_number_id}
                                          </td>
                                          <td className="p-2 border">{num.verified_name || "-"}</td>
                                          <td className="p-2 border">
                                            {acc?.ad_account_name || num.meta_ad_account_id}
                                          </td>
                                          <td className="p-2 border">{num.status}</td>
                                          <td className="p-2 border">
                                            <button
                                              onClick={() => toggleNumberStatus(u.id, num)}
                                              className={`px-2 py-1 rounded text-white mr-1 ${
                                                num.status === "active" ? "bg-green-600" : "bg-gray-500"
                                              }`}
                                            >
                                              {num.status === "active" ? "Active" : "Inactive"}
                                            </button>
                                            <button
                                              onClick={() => deleteWhatsappNumber(u.id, num.id)}
                                              className="px-2 py-1 rounded text-white bg-red-600"
                                            >
                                              Del
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {!whatsappNumbers.length && (
                                      <tr>
                                        <td colSpan="5" className="p-2 text-center text-gray-500">
                                          Koi verified number nahi hai
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white p-3 rounded border grid md:grid-cols-3 gap-2">
                                <select
                                  className="border p-2"
                                  value={newNumber.meta_ad_account_id}
                                  onChange={(e) =>
                                    setNewNumber({ ...newNumber, meta_ad_account_id: e.target.value })
                                  }
                                >
                                  <option value="">Select Ad Account</option>
                                  {adAccounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {a.ad_account_name}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  className="border p-2 md:col-span-2"
                                  placeholder="WhatsApp Phone Number ID (Meta se copy karein)"
                                  value={newNumber.phone_number_id}
                                  onChange={(e) =>
                                    setNewNumber({ ...newNumber, phone_number_id: e.target.value })
                                  }
                                />
                                <button
                                  onClick={() => addWhatsappNumber(u.id)}
                                  disabled={numberSaving}
                                  className="bg-purple-600 text-white px-4 py-2 rounded md:col-span-3 disabled:opacity-50"
                                >
                                  {numberSaving ? "Verifying..." : "+ Verify & Add Number"}
                                </button>
                              </div>
                            </div>

                            <div>
                              <h3 className="font-bold text-purple-800 mb-2">
                                Social Media Pages (Inbox / Comments / Auto-Poster)
                              </h3>

                              <div className="overflow-x-auto">
                                <table className="w-full border text-xs mb-3 bg-white">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="p-2 border">Page Name</th>
                                      <th className="p-2 border">FB Page ID</th>
                                      <th className="p-2 border">Instagram</th>
                                      <th className="p-2 border">FB Inbox</th>
                                      <th className="p-2 border">IG Inbox</th>
                                      <th className="p-2 border">Comments</th>
                                      <th className="p-2 border">Poster</th>
                                      <th className="p-2 border">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {socialPages.map((page) => (
                                      <tr key={page.id} className="text-center">
                                        <td className="p-2 border">{page.page_name}</td>
                                        <td className="p-2 border">{page.facebook_page_id}</td>
                                        <td className="p-2 border">{page.instagram_username || page.instagram_account_id || "-"}</td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => togglePageFeature(u.id, page, "fb_inbox_enabled")}
                                            className={`px-2 py-1 rounded text-white ${
                                              Number(page.fb_inbox_enabled) === 1 ? "bg-green-600" : "bg-gray-500"
                                            }`}
                                          >
                                            {Number(page.fb_inbox_enabled) === 1 ? "ON" : "OFF"}
                                          </button>
                                        </td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => togglePageFeature(u.id, page, "ig_inbox_enabled")}
                                            className={`px-2 py-1 rounded text-white ${
                                              Number(page.ig_inbox_enabled) === 1 ? "bg-green-600" : "bg-gray-500"
                                            }`}
                                          >
                                            {Number(page.ig_inbox_enabled) === 1 ? "ON" : "OFF"}
                                          </button>
                                        </td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => togglePageFeature(u.id, page, "comment_bot_enabled")}
                                            className={`px-2 py-1 rounded text-white ${
                                              Number(page.comment_bot_enabled) === 1 ? "bg-green-600" : "bg-gray-500"
                                            }`}
                                          >
                                            {Number(page.comment_bot_enabled) === 1 ? "ON" : "OFF"}
                                          </button>
                                        </td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => togglePageFeature(u.id, page, "poster_enabled")}
                                            className={`px-2 py-1 rounded text-white ${
                                              Number(page.poster_enabled) === 1 ? "bg-green-600" : "bg-gray-500"
                                            }`}
                                          >
                                            {Number(page.poster_enabled) === 1 ? "ON" : "OFF"}
                                          </button>
                                        </td>
                                        <td className="p-2 border">
                                          <button
                                            onClick={() => deleteSocialPage(u.id, page.id)}
                                            className="px-2 py-1 rounded text-white bg-red-600"
                                          >
                                            Del
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                    {!socialPages.length && (
                                      <tr>
                                        <td colSpan="8" className="p-2 text-center text-gray-500">
                                          Koi social page connect nahi hai
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white p-3 rounded border grid md:grid-cols-3 gap-2">
                                <input
                                  className="border p-2"
                                  placeholder="Page Name (jaise 'Rudhvee Clinic')"
                                  value={newPage.page_name}
                                  onChange={(e) => setNewPage({ ...newPage, page_name: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Facebook Page ID"
                                  value={newPage.facebook_page_id}
                                  onChange={(e) => setNewPage({ ...newPage, facebook_page_id: e.target.value })}
                                />
                                <input
                                  className="border p-2"
                                  placeholder="Instagram Account ID (optional)"
                                  value={newPage.instagram_account_id}
                                  onChange={(e) => setNewPage({ ...newPage, instagram_account_id: e.target.value })}
                                />
                                <input
                                  className="border p-2 md:col-span-3"
                                  placeholder="Page Access Token (Social System User se)"
                                  value={newPage.page_access_token}
                                  onChange={(e) => setNewPage({ ...newPage, page_access_token: e.target.value })}
                                />
                                <button
                                  onClick={() => addSocialPage(u.id)}
                                  disabled={pageSaving}
                                  className="bg-purple-600 text-white px-4 py-2 rounded md:col-span-3 disabled:opacity-50"
                                >
                                  {pageSaving ? "Verifying..." : "+ Verify & Add Page"}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {users.length === 0 && (
                <tr>
                  <td colSpan="20" className="p-4 text-center">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}